import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  computed,
  signal,
} from '@angular/core';

const ZOOM_STEPS = [1, 1.5, 2.5, 4];

@Component({
  selector: 'app-image-lightbox',
  standalone: true,
  templateUrl: './image-lightbox.component.html',
  styleUrl: './image-lightbox.component.css',
})
export class ImageLightboxComponent implements OnDestroy {
  @Input({ required: true }) src = '';
  @Input() alt = '';

  @ViewChild('closeBtn') private closeBtn?: ElementRef<HTMLButtonElement>;

  protected readonly isOpen = signal(false);
  private readonly zoomIndex = signal(0);
  private readonly offset = signal({ x: 0, y: 0 });
  private dragging = false;
  private didDrag = false;
  private dragStart = { x: 0, y: 0 };
  private offsetStart = { x: 0, y: 0 };
  private triggerEl?: HTMLElement;

  protected readonly imageTransform = computed(() => {
    const zoom = ZOOM_STEPS[this.zoomIndex()] ?? 1;
    const { x, y } = this.offset();
    return `translate(${x}px, ${y}px) scale(${zoom})`;
  });

  ngOnDestroy(): void {
    this.unlockBody();
    this.removeMoveListeners();
  }

  protected open(): void {
    this.triggerEl = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.isOpen.set(true);
    this.zoomIndex.set(0);
    this.offset.set({ x: 0, y: 0 });
    document.body.classList.add('image-lightbox-open');
    queueMicrotask(() => this.closeBtn?.nativeElement.focus());
  }

  protected close(): void {
    if (!this.isOpen()) {
      return;
    }
    this.isOpen.set(false);
    this.unlockBody();
    this.triggerEl?.focus();
  }

  protected zoomIn(): void {
    this.zoomIndex.update((index) => Math.min(index + 1, ZOOM_STEPS.length - 1));
  }

  protected zoomOut(): void {
    this.zoomIndex.update((index) => {
      const next = Math.max(index - 1, 0);
      if (next === 0) {
        this.offset.set({ x: 0, y: 0 });
      }
      return next;
    });
  }

  protected onImageClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.didDrag) {
      this.didDrag = false;
      return;
    }
    if (this.zoomIndex() >= ZOOM_STEPS.length - 1) {
      this.zoomIndex.set(0);
      this.offset.set({ x: 0, y: 0 });
      return;
    }
    this.zoomIn();
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this.zoomIndex() === 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.didDrag = false;
    this.dragStart = { x: event.clientX, y: event.clientY };
    this.offsetStart = this.offset();
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.close();
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.dragging) {
      return;
    }
    const next = {
      x: this.offsetStart.x + event.clientX - this.dragStart.x,
      y: this.offsetStart.y + event.clientY - this.dragStart.y,
    };
    if (Math.abs(next.x - this.offsetStart.x) > 4 || Math.abs(next.y - this.offsetStart.y) > 4) {
      this.didDrag = true;
    }
    this.offset.set(next);
  };

  private readonly onPointerUp = (): void => {
    this.dragging = false;
    this.removeMoveListeners();
  };

  private unlockBody(): void {
    document.body.classList.remove('image-lightbox-open');
  }

  private removeMoveListeners(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }
}
